import { useQuery } from '@apollo/react-hooks';
import graphqlTag from 'graphql-tag';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import DateTimePicker from 'components/DateTimePicker';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import PageHeaderIcon from 'components/PageHeaderIcon';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import { PayoutActionsModal } from './PayoutActionsModal';

const GET_PAYOUT_EVENTS = graphqlTag`
  query GetPayoutEvents($input: GetPayoutEventsAdminInput!) {
    getPayoutEventsAdmin(input: $input) {
      count
      results {
        id
        name
        event_date
        account_id
        account_business_dba
        stripe_account_id
        stripe_balance
        payout_balance
        payout_status
      }
    }
  }
`;

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

const ContentWrapper = styled.div`
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const DateFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 180px;
`;

const DateLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#475569')};
`;

const PAYOUT_STATUS_META = {
  red: {
    label: 'Unpaid',
    color: '#f87171',
    background: 'rgba(239, 68, 68, 0.15)',
    border: 'rgba(239, 68, 68, 0.3)',
  },
  yellow: {
    label: 'Pending',
    color: '#facc15',
    background: 'rgba(234, 179, 8, 0.15)',
    border: 'rgba(234, 179, 8, 0.3)',
  },
  green: {
    label: 'Paid',
    color: '#4ade80',
    background: 'rgba(34, 197, 94, 0.15)',
    border: 'rgba(34, 197, 94, 0.3)',
  },
};

const PAYOUT_STATUS_UNSET = {
  label: 'Not Set',
  color: '#94a3b8',
  background: 'rgba(148, 163, 184, 0.15)',
  border: 'rgba(148, 163, 184, 0.3)',
};

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${(props) => props.$meta.background};
  border: 1px solid ${(props) => props.$meta.border};
  color: ${(props) => props.$meta.color};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 0 3px ${(props) => props.$meta.background};
`;

const renderPayoutStatus = (event) => {
  const meta = PAYOUT_STATUS_META[event.payout_status] || PAYOUT_STATUS_UNSET;
  return (
    <StatusPill $meta={meta}>
      <StatusDot $meta={meta} />
      {meta.label}
    </StatusPill>
  );
};

const BalanceText = styled.span`
  color: ${(props) => (props.$mismatch ? '#facc15' : 'inherit')};
  font-weight: ${(props) => (props.$mismatch ? 600 : 'inherit')};
`;

const balancesMismatch = (event) =>
  Boolean(event.stripe_balance) &&
  Boolean(event.payout_balance) &&
  event.stripe_balance !== event.payout_balance;

const renderStripeBalance = (event) => (
  <BalanceText $mismatch={balancesMismatch(event)}>
    {event.stripe_balance || '—'}
  </BalanceText>
);

const renderPayoutBalance = (event) => (
  <BalanceText $mismatch={balancesMismatch(event)}>
    {event.payout_balance || '—'}
  </BalanceText>
);

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatGraphQLDate = (dateInput) => {
  if (!dateInput) return '';
  const [year, month, day] = dateInput.split('-');
  return `${month}-${day}-${year}`;
};

const formatEventDate = (dateValue) => {
  if (!dateValue) return '—';

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const getStripeDashboardMode = () => {
  const dashboardMode = process.env.REACT_APP_STRIPE_DASHBOARD_MODE;

  if (dashboardMode === 'live' || dashboardMode === 'test') {
    return dashboardMode;
  }

  const stripePaymentKey = process.env.REACT_APP_STRIPE_PAYMENT_KEY || '';

  return stripePaymentKey.includes('pk_live_') ? 'live' : 'test';
};

const getStripeDashboardUrl = (stripeAccountId) => {
  const platformStripeAccountId =
    process.env.REACT_APP_STRIPE_PLATFORM_ACCOUNT_ID;

  if (!stripeAccountId || !platformStripeAccountId) return null;

  const testSegment = getStripeDashboardMode() === 'test' ? '/test' : '';

  return `https://dashboard.stripe.com/${platformStripeAccountId}${testSegment}/connect/accounts/${stripeAccountId}/activity`;
};

const getPreviousWeekRange = () => {
  const today = new Date();
  const currentDay = today.getDay();
  const daysSinceMonday = (currentDay + 6) % 7;

  const previousMonday = new Date(today);
  previousMonday.setHours(0, 0, 0, 0);
  previousMonday.setDate(today.getDate() - daysSinceMonday - 7);

  const previousSunday = new Date(previousMonday);
  previousSunday.setDate(previousMonday.getDate() + 6);

  return {
    startDate: formatDateInput(previousMonday),
    endDate: formatDateInput(previousSunday),
  };
};

const PayoutsPage = () => {
  const defaultRange = useMemo(() => getPreviousWeekRange(), []);
  const { input } = useSearchInput();
  const updateQuery = useUpdateQuery();

  const currentPage = input.page ? Number(input.page) : 1;
  const pageLimit = 15;

  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [activeEvent, setActiveEvent] = useState(null);

  const { data, loading, refetch } = useQuery(GET_PAYOUT_EVENTS, {
    variables: {
      input: {
        page: String(currentPage),
        limit: String(pageLimit),
        start_date: formatGraphQLDate(startDate),
        end_date: formatGraphQLDate(endDate),
      },
    },
  });

  const events = data?.getPayoutEventsAdmin?.results ?? [];
  const totalCount = data?.getPayoutEventsAdmin?.count ?? 0;
  const totalPages = Math.ceil(totalCount / pageLimit);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const columns = [
    {
      field: 'payout_status',
      header: 'Payout Status',
      render: renderPayoutStatus,
    },
    {
      field: 'account_business_dba',
      header: 'Account',
      render: (event) => event.account_business_dba || '—',
    },
    {
      field: 'event_date',
      header: 'Event Date',
      render: (event) => formatEventDate(event.event_date),
    },
    {
      field: 'name',
      header: 'Event',
      render: (event) => event.name || 'Unnamed Event',
    },
    {
      field: 'stripe_balance',
      header: 'Stripe Balance',
      render: renderStripeBalance,
    },
    {
      field: 'payout_balance',
      header: 'Payout Balance',
      render: renderPayoutBalance,
    },
  ];

  const actions = [
    {
      type: 'view',
      title: 'View Event',
      to: (event) => `/admin/events/menu/${event.id}`,
    },
    {
      title: 'Payout Actions',
      mobileLabel: 'Payout',
      onClick: (event) => {
        setActiveEvent(event);
      },
      icon: (
        <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
          <path d="M4 10.781c.148 1.667 1.513 2.85 3.591 3.003V15h1.043v-1.216c2.27-.179 3.678-1.438 3.678-3.3 0-1.59-.947-2.51-2.956-3.028l-.722-.187V3.467c1.122.11 1.879.714 2.07 1.616h1.47c-.166-1.6-1.54-2.748-3.54-2.875V1H7.591v1.233c-1.939.23-3.27 1.472-3.27 3.156 0 1.454.966 2.483 2.661 2.917l.61.162v4.031c-1.149-.17-1.94-.8-2.131-1.718H4zm3.391-3.836c-1.043-.263-1.6-.825-1.6-1.616 0-.944.704-1.641 1.8-1.828v3.495l-.2-.05zm1.591 1.872c1.287.323 1.852.859 1.852 1.769 0 1.097-.826 1.828-2.2 1.939V8.73l.348.086z" />
        </svg>
      ),
    },
  ];

  const emptyIcon = (
    <svg
      width="48"
      height="48"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      style={{ marginBottom: '16px', opacity: 0.5 }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );

  return (
    <EnhancedThemeProvider>
      <div style={{ background: 'transparent', minHeight: '100vh' }}>
        <PageContainer>
          <Card style={{ marginBottom: '24px' }}>
            <ContentWrapper>
              <HeaderContainer>
                <PageHeaderIcon icon="transactions" />
                <HeaderTitle>Payouts</HeaderTitle>
              </HeaderContainer>
            </ContentWrapper>
          </Card>

          <Card>
            <ContentWrapper>
              <Toolbar>
                <DateFieldGroup>
                  <DateLabel htmlFor="payout-start-date">Start Date</DateLabel>
                  <DateTimePicker
                    value={startDate}
                    onChange={setStartDate}
                    showTime={false}
                    placeholder="MM/DD/YYYY"
                    cancelText="Clear"
                  />
                </DateFieldGroup>

                <DateFieldGroup>
                  <DateLabel htmlFor="payout-end-date">End Date</DateLabel>
                  <DateTimePicker
                    value={endDate}
                    onChange={setEndDate}
                    showTime={false}
                    placeholder="MM/DD/YYYY"
                    cancelText="Clear"
                  />
                </DateFieldGroup>
              </Toolbar>

              <DataTable
                columns={columns}
                data={events}
                loading={loading}
                keyField="id"
                emptyMessage="No payout events found"
                emptySubtext="Try adjusting your date range"
                emptyIcon={emptyIcon}
                actions={actions}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                mobileQuickInfo={[
                  {
                    field: 'event_date',
                    label: 'Event Date',
                    render: (event) => formatEventDate(event.event_date),
                  },
                  {
                    field: 'stripe_balance',
                    label: 'Stripe Balance',
                    render: renderStripeBalance,
                  },
                  {
                    field: 'payout_balance',
                    label: 'Payout Balance',
                    render: renderPayoutBalance,
                  },
                  {
                    field: 'payout_status',
                    label: 'Payout Status',
                    render: renderPayoutStatus,
                  },
                ]}
              />
            </ContentWrapper>
          </Card>
        </PageContainer>

        <PayoutActionsModal
          isOpen={Boolean(activeEvent)}
          onClose={() => setActiveEvent(null)}
          event={activeEvent}
          stripeUrl={
            activeEvent
              ? getStripeDashboardUrl(activeEvent.stripe_account_id)
              : null
          }
          onStatusChanged={refetch}
        />
      </div>
    </EnhancedThemeProvider>
  );
};

export default PayoutsPage;
